import React, { useState, useEffect } from "react";
import { Plus, Search, Filter, Edit, Eye, Trash2, User, X, FileText } from "lucide-react";
import apiService from "../../services/apiService";
import MembersSkeleton from "./MembersSkeleton";

const MembersManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // modal state
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showView, setShowView] = useState(false);
  const [viewMember, setViewMember] = useState(null);
  
  // Policy assignment state
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [assigningMember, setAssigningMember] = useState(null);
  const [availablePolicies, setAvailablePolicies] = useState([]);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  
  // memeber async function
  const getMembersAsync = async () => {
    try {
      const result = await apiService.getMembers();
      // assume result is array of members
      if (result && Array.isArray(result?.data)) setMembers(result?.data);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setLoading(false);
    }
  };
  // load members
  useEffect(() => {
    getMembersAsync();
  }, []);

  // filtered list
  const filteredMembers = members.filter((member) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term ||
      member?.user.name?.toLowerCase().includes(term) ||
      member?.user.email?.toLowerCase().includes(term) ||
      member.member_number?.toLowerCase().includes(term || "");
    const matchesStatus =
      filterStatus === "all" || member?.user?.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Form component for create/edit
  const MemberForm = ({ member, onSave, onCancel }) => {
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
      name: member?.user?.name || "",
      email: member?.user?.email || "",
      password: "", // when editing, leave blank => backend should ignore if empty
      phone: member?.user?.phone || "",
      date_of_birth: member?.date_of_birth || "",
      address: member?.address || "",
      emergency_contact: {
        name: member?.emergency_contact?.name || "",
        relationship: member?.emergency_contact?.relationship || "",
        phone: member?.emergency_contact?.phone || "",
      },
      // optionally include status if backend supports it
      status: member?.user?.status || "active",
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      // if editing and password blank, omit password
      const payload = { ...formData };
      if (editingMember && !payload.password) delete payload.password;

      try {
        setFormLoading(true);

        let result;
        if (member) {
          result = await apiService.updateMember(member.id, payload);
        } else {
          result = await apiService.createMember(payload);
        }
        console.log(result);
        if (onSave) {
          onSave(result?.data);
          getMembersAsync();
        }
      } catch (error) {
        console.error("Error saving member:", error);
        // you may want to show toasts/errors here
      } finally {
        setFormLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {member ? "Edit Member" : "Create New Member"}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password {member ? "(leave blank to keep)" : ""}
                </label>
                <input
                  type="password"
                  minlength="8"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  {...(!member ? { required: true } : {})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    setFormData({ ...formData, date_of_birth: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="2"
              />
            </div>

            {/* Emergency contact */}
            <div className="border rounded p-3 bg-gray-50">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Emergency Contact
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.emergency_contact.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_contact: {
                          ...formData.emergency_contact,
                          name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Relationship
                  </label>
                  <input
                    type="text"
                    value={formData.emergency_contact.relationship}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_contact: {
                          ...formData.emergency_contact,
                          relationship: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={formData.emergency_contact.phone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_contact: {
                          ...formData.emergency_contact,
                          phone: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
                disabled={formLoading}
              >
                {formLoading ? (
                  "Saving..."
                ) : (
                  <>{member ? "Update Member" : "Create Member"}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    try {
      await apiService.deleteMember(id);
      setMembers((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error("Failed to delete member:", err);
    }
  };

  // Open create modal
  const openCreate = () => {
    setEditingMember(null);
    setShowForm(true);
  };

  // Load policies
  const loadPolicies = async () => {
    try {
      setPolicyLoading(true);
      const result = await apiService.getPolicies();
      if (result && Array.isArray(result)) {
        setAvailablePolicies(result);
      }
    } catch (err) {
      console.error("Failed to load policies:", err);
    } finally {
      setPolicyLoading(false);
    }
  };

  // Open policy assignment modal
  const openPolicyAssignment = async (member) => {
    setAssigningMember(member);
    setShowPolicyModal(true);
    
    try {
      setPolicyLoading(true);
      
      // Fetch member details to get assigned policies
      const memberResult = await apiService.getMemberById(member.id);
      const memberData = memberResult;
      
      // Extract already assigned policy IDs
      const assignedPolicyIds = memberData?.policies?.map(policy => policy.id) || [];
      setSelectedPolicies(assignedPolicyIds);
      
      // Load available policies
      await loadPolicies();
    } catch (err) {
      console.error("Failed to load member details:", err);
      setSelectedPolicies([]);
      await loadPolicies();
    }
  };

  // Handle policy selection
  const togglePolicySelection = (policyId) => {
    setSelectedPolicies((prev) => {
      if (prev.includes(policyId)) {
        return prev.filter((id) => id !== policyId);
      } else {
        return [...prev, policyId];
      }
    });
  };

  // Save assigned policies
  const handleAssignPolicies = async () => {
    try {
      setPolicyLoading(true);
      // Call API to assign policies to member
      await apiService.assignPoliciesToMember(assigningMember.id, selectedPolicies);
      console.log("Assigning policies:", selectedPolicies, "to member:", assigningMember.id);
      
      // Refresh members list
      await getMembersAsync();
      
      setShowPolicyModal(false);
      setAssigningMember(null);
      setSelectedPolicies([]);
    } catch (err) {
      console.error("Failed to assign policies:", err);
    } finally {
      setPolicyLoading(false);
    }
  };

  // Save callback after create/update
  const handleSave = (saved) => {
    if (!saved) {
      setShowForm(false);
      setEditingMember(null);
      return;
    }
    // if backend returns the created/updated member object
    if (editingMember) {
      setMembers((prev) => prev.map((m) => (m.id === saved.id ? saved : m)));
    } else {
      setMembers((prev) => [saved, ...prev]);
    }
    setShowForm(false);
    setEditingMember(null);
  };

  if (loading) return <MembersSkeleton />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Member Management
          </h2>
          <p className="text-gray-600 mt-1">
            Manage insurance members and their policies
          </p>
        </div>

        <button
          onClick={openCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Member
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policies
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.map((member) => (
                <tr key={member?.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {member?.user?.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {member?.member_number || ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {member?.user?.email}
                    </div>
                    <div className="text-sm text-gray-500">
                      {member?.user?.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(member.policies) &&
                      member.policies.length > 0 ? (
                        member.policies.map((policy, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {policy.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        member?.user?.status === "active"
                          ? "bg-green-100 text-green-800"
                          : member?.user?.status === "suspended"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {member?.user?.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openPolicyAssignment(member)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Assign Policy"
                      >
                        <FileText className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => {
                          setEditingMember(member);
                          setShowForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      {/* <button
                        onClick={() => {
                          setViewMember(member);
                          setShowView(true);
                        }}
                        className="text-green-600 hover:text-green-900"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button> */}

                      <button
                        onClick={() => handleDelete(member.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredMembers.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    No members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <MemberForm
          member={editingMember}
          onSave={(saved) => {
            // saved may be the returned member object from backend
            if (!saved) {
              // close only
              setShowForm(false);
              setEditingMember(null);
              return;
            }

            if (editingMember) {
              setMembers((prev) =>
                prev.map((m) => (m.id === saved.id ? saved : m))
              );
            } else {
              setMembers((prev) => [saved, ...prev]);
            }
            setShowForm(false);
            setEditingMember(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingMember(null);
          }}
        />
      )}

      {/* Policy Assignment Modal */}
      {showPolicyModal && assigningMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Assign Policies
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Member: {assigningMember?.user?.name}
                </p>
                <p className="text-xs text-gray-500">
                  ID: {assigningMember?.member_number || assigningMember?.id}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPolicyModal(false);
                  setAssigningMember(null);
                  setSelectedPolicies([]);
                }}
                className="text-gray-500 hover:text-gray-700"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {policyLoading ? (
              <div className="text-center py-8 text-gray-500">
                Loading policies...
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-3">
                    Select policies to assign (multiple selection allowed):
                  </p>
                  
                  {/* None option */}
                  <div className="border rounded-lg mb-2">
                    <label className="flex items-start p-4 cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedPolicies.length === 0}
                        onChange={() => setSelectedPolicies([])}
                        className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">None</div>
                        <div className="text-sm text-gray-500">
                          No policy assigned
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Available policies */}
                  <div className="space-y-2">
                    {availablePolicies.map((policy) => (
                      <div
                        key={policy.id}
                        className="border rounded-lg hover:border-blue-300 transition-colors"
                      >
                        <label className="flex items-start p-4 cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedPolicies.includes(policy.id)}
                            onChange={() => togglePolicySelection(policy.id)}
                            className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="ml-3 flex-1">
                            <div className="font-medium text-gray-900">
                              {policy.name}
                            </div>
                            <div className="text-sm text-gray-600 mt-1 flex items-center space-x-4">
                              <span>
                                Premium: <span className="font-medium">${policy.premium_amount}</span>
                              </span>
                              <span>
                                Deductible: <span className="font-medium">${policy.deductible}</span>
                              </span>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>

                  {availablePolicies.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No policies available
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPolicyModal(false);
                      setAssigningMember(null);
                      setSelectedPolicies([]);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAssignPolicies}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={policyLoading}
                  >
                    {policyLoading ? "Assigning..." : "Assign Policies"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* View modal */}
      {showView && viewMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">{viewMember.name}</h3>
                <p className="text-sm text-gray-500">{viewMember.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowView(false);
                  setViewMember(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <div className="text-xs text-gray-500">Phone</div>
                <div className="text-sm text-gray-900">
                  {viewMember.phone || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Date of Birth</div>
                <div className="text-sm text-gray-900">
                  {viewMember.date_of_birth || "—"}
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs text-gray-500">Address</div>
                <div className="text-sm text-gray-900">
                  {viewMember.address || "—"}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs text-gray-500">Emergency Contact</div>
              <div className="text-sm text-gray-900">
                {viewMember.emergency_contact ? (
                  <>
                    <div>{viewMember.emergency_contact.name}</div>
                    <div className="text-xs text-gray-500">
                      {viewMember.emergency_contact.relationship} — {viewMember.emergency_contact.phone}
                    </div>
                  </>
                ) : (
                  <div className="text-gray-400">—</div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowView(false);
                  setViewMember(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersManager;